import React from 'react';
import PropTypes from "prop-types";
import { useSelector } from 'react-redux';
import { Field, touch, change } from 'redux-form';
import { ReduxControls } from "rhombus-react";

import { getCurrentFieldNames, getFormStateByAdType } from 'selectors/Social2';
import { hasVersionsSelector } from "selectors/Creatives";

import PACHeader from 'social/PACHeader';
import { PAC_FORM_TYPES, PAC_TYPES } from 'social/constants';
import { SOCIAL_WIZARD } from "widgets/forms/list";
import PlacementSelector from 'social/PlacementSelector';
import PlacementForm from 'social/PlacementForm';
import { isDynamic } from 'social/DynamicCreativeToggle';
import { assetType } from 'social/BuildMethodRadio';

import { SOCIAL_AD_TYPE_LIST, DYNAMIC_SOCIAL_AD_TYPES } from 'utils/constants';
import PlacementErrorBanner from 'social/PlacementErrorBanner';

import "./PACController.css";

function setActivePlacementForAdType(adType){
	/*
		This does partial application on an action creator, binding the adType, to be
		dispatched by child components because there were some issues with the selected
		_currentActivePAC state being out of sync with the child components.

		Because it can just be selected for, along with the current Field names, we can just
		make it an action creator that uses getState to be confident about sync'd state.
	*/

	return (type) => {
		return (dispatch, getState) => {
			const state = getState();
			const currentFieldNames = getCurrentFieldNames(state);
			const currentType = getFormStateByAdType(state, adType)?.values?._currentActivePAC || PAC_FORM_TYPES.DEFAULT;

			dispatch(touch(SOCIAL_WIZARD, ...currentFieldNames)); // Touch the fields to trigger validation
			if(currentType !== type){
				dispatch(change(SOCIAL_WIZARD, `${adType}._currentActivePAC`, type));
			}
		};
	};
}

export default function PACController(props) {
	const {
		defaultForm,
		adType,
		isViewOnly,
		showPristineErrors,
		selectProps,
		headerProps,
	} = props;

	const doVersionsExist = useSelector(hasVersionsSelector);
	const { noAssetCustomizeTooltip, customizeAssetTooltip } = headerProps;
	const { values } = useSelector(state => getFormStateByAdType(state, adType));
	const {
		placementAssetCustomizationType: pacType = PAC_TYPES.NONE,
		_currentActivePAC = PAC_FORM_TYPES.DEFAULT,
	} = values;

	const isNew = useSelector((state) => {
		const { values } = getFormStateByAdType(state, adType);
		return values.placements?.[_currentActivePAC]?._isNew;
	});

	const isDefault = (_currentActivePAC === PAC_FORM_TYPES.DEFAULT) || (pacType === PAC_TYPES.NONE);
	const form = isDefault ? defaultForm : <PlacementForm placementType={_currentActivePAC} {...props} />;
	const setActivePlacement = setActivePlacementForAdType(adType);

	const isDynamicType = DYNAMIC_SOCIAL_AD_TYPES.includes(adType);
	const shouldShowDynamicControls = isDynamicType && !isDefault;

	let dynamicControls = null;
	if(shouldShowDynamicControls){
		dynamicControls = (
			<div className="fields">
				<Field {...isDynamic} disabled />
				<Field {...assetType} disabled />
			</div>
		);
	}

	let headerSelect;
	if (pacType !== PAC_TYPES.NONE) {
		headerSelect = (
			<PlacementSelector
				adType = {adType}
				showPristineErrors = {showPristineErrors}
				isViewOnly = {isViewOnly || doVersionsExist}
				setActivePlacement = {setActivePlacement}
				selectProps = {selectProps}
			/>
		);
	}

	return (
		<div className="pac-controller">
			<div className="header-container">
				<div className="pac-checkbox-container">
					<div data-tip={noAssetCustomizeTooltip}>
						<Field
							label="No Asset Customization"
							type="radio"
							className="pac-checkbox"
							value={PAC_TYPES.NONE}
							name="placementAssetCustomizationType"
							component={ReduxControls.Checkbox}
							checked={pacType === PAC_TYPES.NONE}
							disabled={isViewOnly || doVersionsExist}
						/>
					</div>
					<div data-tip={customizeAssetTooltip}>
						<Field
							label="Customize by Aspect Ratio/Placement"
							type="radio"
							className="pac-checkbox"
							value={PAC_TYPES.ASPECT_RATIO}
							name="placementAssetCustomizationType"
							component={ReduxControls.Checkbox}
							checked={pacType === PAC_TYPES.ASPECT_RATIO}
							disabled={isViewOnly || doVersionsExist}
						/>
					</div>
				</div>
				{headerSelect}
			</div>
			<div className="form-container">
				<PlacementErrorBanner
					adType={adType}
					placementType={_currentActivePAC}
					showPristineErrors={!isNew && showPristineErrors}
				/>
				{(pacType !== PAC_TYPES.NONE) && <PACHeader
					adType = {adType}
					pacType = {pacType}
					isViewOnly = {isViewOnly}
					setActivePlacement = {setActivePlacement}
				/>}
				{dynamicControls}
				{form}
			</div>
		</div>
	);
}

PACController.displayName = "FacebookPACController";

PACController.defaultProps = {
	headerProps: {},
};

PACController.propTypes = {
	defaultForm: PropTypes.node.isRequired,
	defaultFieldGenerator: PropTypes.func.isRequired,
	adType: PropTypes.oneOf(SOCIAL_AD_TYPE_LIST).isRequired,
	headerProps: PropTypes.object,
};
